import React, { useEffect, useMemo, useState } from "react";
import { X, Check, ArrowRight, Wand2, ShieldCheck } from "lucide-react";

const isMeaningful = (v) => v !== undefined && v !== null && v !== "" && !(typeof v === "number" && v === 0);
const show = (v) => (isMeaningful(v) ? String(v) : "—");

export default function OcrReviewModal({ open, onClose, detected = {}, current = {}, fields = [], onApply, confidence, summary }) {
  const meaningful = useMemo(() => fields.filter((f) => isMeaningful(detected[f.key])), [fields, detected]);
  const [sel, setSel] = useState({});
  useEffect(() => {
    if (open) { const s = {}; meaningful.forEach((f) => { s[f.key] = true; }); setSel(s); }
  }, [open, meaningful]);
  if (!open) return null;

  const allOn = meaningful.length > 0 && meaningful.every((f) => sel[f.key]);
  const toggleAll = () => { const s = {}; meaningful.forEach((f) => { s[f.key] = !allOn; }); setSel(s); };
  const apply = () => {
    const out = {};
    meaningful.forEach((f) => { if (sel[f.key]) out[f.key] = detected[f.key]; });
    onApply(out);
    onClose();
  };
  const confCls = { "Élevée": "bg-emerald-100 text-emerald-700", "Moyenne": "bg-amber-100 text-amber-700", "Faible": "bg-red-100 text-red-600" }[confidence] || "bg-brand-soft text-brand-green";

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[88vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="ocr-review-modal">
        <div className="px-6 py-4 border-b border-brand-soft flex items-start justify-between gap-3">
          <div>
            <h3 className="font-display text-lg font-semibold text-brand-greenDeep flex items-center gap-2"><Wand2 className="w-5 h-5 text-brand-limeDark" /> Valeurs détectées — vérifiez avant d'appliquer</h3>
            <div className="flex items-center gap-2 mt-1">
              {confidence && <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${confCls}`} data-testid="ocr-confidence"><ShieldCheck className="w-3 h-3" /> Confiance : {confidence}</span>}
              {summary && <span className="text-xs text-brand-green/55">{summary}</span>}
            </div>
          </div>
          <button onClick={onClose} data-testid="ocr-review-close"><X className="w-5 h-5 text-brand-green/50" /></button>
        </div>

        <div className="overflow-y-auto px-6 py-4">
          {meaningful.length === 0 ? (
            <p className="text-sm text-brand-green/50 py-8 text-center" data-testid="ocr-review-empty">Aucune donnée exploitable n'a été détectée dans les documents.</p>
          ) : (
            <>
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs text-brand-green/55">Cochez les champs à appliquer au formulaire. Les valeurs actuelles seront remplacées.</p>
                <button onClick={toggleAll} className="text-xs font-semibold text-brand-greenDeep hover:underline" data-testid="ocr-toggle-all">{allOn ? "Tout décocher" : "Tout cocher"}</button>
              </div>
              <div className="space-y-1.5">
                {meaningful.map((f) => (
                  <label key={f.key} className={`flex items-center gap-3 rounded-xl px-3 py-2.5 cursor-pointer border ${sel[f.key] ? "border-brand-lime bg-brand-lime/10" : "border-brand-soft"}`} data-testid={`ocr-row-${f.key}`}>
                    <input type="checkbox" checked={!!sel[f.key]} onChange={(e) => setSel((s) => ({ ...s, [f.key]: e.target.checked }))} className="w-4 h-4 accent-brand-limeDark" data-testid={`ocr-check-${f.key}`} />
                    <span className="text-sm font-medium text-brand-greenDeep w-40 shrink-0">{f.label}</span>
                    <span className="text-sm text-brand-green/45 line-clamp-1 flex-1 text-right">{show(current[f.key])}</span>
                    <ArrowRight className="w-4 h-4 text-brand-green/30 shrink-0" />
                    <span className="text-sm font-semibold text-brand-greenDeep flex-1 line-clamp-2">{show(detected[f.key])}</span>
                  </label>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="px-6 py-4 border-t border-brand-soft flex gap-3">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-full border border-brand-soft text-brand-green font-semibold text-sm" data-testid="ocr-review-cancel">Annuler</button>
          <button onClick={apply} disabled={meaningful.length === 0} className="flex-1 py-2.5 rounded-full bg-brand-greenDeep hover:bg-brand-green text-white font-semibold text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50" data-testid="ocr-review-apply"><Check className="w-4 h-4" /> Appliquer la sélection</button>
        </div>
      </div>
    </div>
  );
}
